import React, { SyntheticEvent, useEffect, useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";

import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { useTranslation } from "react-i18next";
import { ApiTokenPreview as IApiTokenPreview } from "./ManageApiTokens";

interface ApiTokenPreviewProps {
  /**
   * The ApiTokenPreview to show information for.
   */
  apiTokenPreview: IApiTokenPreview | null;
  /**
   * If `true`, the component is shown.
   * @default false
   */
  isOpen: boolean;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;
}

export default function ApiTokenPreview(props: ApiTokenPreviewProps): React.JSX.Element {
  const i18n = useTranslation();

  const [isConfirmed, setIsConfirmed] = useState(false);

  function onCopyDescriptionToClipboard(): void {
    if (props.apiTokenPreview) {
      navigator.clipboard.writeText(props.apiTokenPreview.description);
    }
  };

  function onCopyApiTokenToClipboard(): void {
    if (props.apiTokenPreview) {
      navigator.clipboard.writeText(props.apiTokenPreview.access_token);
    }
  }

  function handleConfirmToggle(event: SyntheticEvent, checked: boolean): void {
    setIsConfirmed(checked);
  }

  function handleClose(): void {
    props.onClose();
  }

  useEffect(() => {
    if (props.isOpen) {
      setIsConfirmed(false);
    }
  }, [props.isOpen]);

  return (
    <Dialog open={props.isOpen}>
      <DialogTitle>
        <Typography variant="h3">{i18n.t("api-token-preview.title")}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body1">{i18n.t("api-token-preview.flavor-text")}</Typography>
          <Typography variant="body1">{i18n.t("api-token-preview.security-info")}</Typography>
          <Typography variant="body1">{i18n.t("api-token-preview.troubleshooting")}</Typography>
        </Stack>
        <Stack spacing={1} sx={{
          marginTop: 2
        }}>
          <Box>{i18n.t("api-token-preview.description-field")}</Box>
          <Stack direction="row">
            <TextField fullWidth autoComplete="off" label="" value={props.apiTokenPreview?.description ?? ""} size="small" slotProps={{
              input: {readOnly: true}
            }} />
            <Tooltip title={i18n.t("api-token-preview.description-field-copy")}>
              <IconButton onClick={onCopyDescriptionToClipboard}>
                <ContentPasteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box>{i18n.t("api-token-preview.token-id-field")}</Box>
          <Stack direction="row">
            <TextField fullWidth autoComplete="off" label="" value={props.apiTokenPreview?.access_token ?? ""} size="small" slotProps={{
              input: {readOnly: true}
            }} />
            <Tooltip title={i18n.t("api-token-preview.token-id-field-copy")}>
              <IconButton onClick={onCopyApiTokenToClipboard}>
                <ContentPasteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            marginTop: 2,
            justifyContent: "space-between"
          }}>
          <FormControlLabel control={<Checkbox value={isConfirmed} onChange={handleConfirmToggle} />} label={i18n.t("api-token-preview.user-confirmation")} />
          <Button disabled={!isConfirmed} sx={{width: 125}} variant="contained" onClick={handleClose}>{i18n.t("api-token-preview.close")}</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}